// Thème de l'interface : clair, sombre, ou celui du système (réglage de l'appareil).
// Le choix est retenu dans le navigateur ; la classe « dark » est posée sur <html> avant le premier
// affichage par le script de index.html (pas de flash blanc), puis tenue à jour ici.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useI18n } from '../i18n';

export type ThemeMode = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'zos-theme';
const MODES: ThemeMode[] = ['light', 'dark', 'system'];

function savedMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch { return 'system'; }
}
const systemDark = () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

function apply(dark: boolean) {
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
}

interface ThemeContextType {
  mode: ThemeMode;
  /** Thème effectivement affiché. */
  dark: boolean;
  setMode: (m: ThemeMode) => void;
}
const ThemeContext = createContext<ThemeContextType | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(savedMode);
  const [sysDark, setSysDark] = useState(systemDark);
  const dark = mode === 'dark' || (mode === 'system' && sysDark);

  // suit le réglage de l'appareil en mode « Système »
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = () => setSysDark(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => { apply(dark); }, [dark]);

  const setMode = useCallback((m: ThemeMode) => {
    try { localStorage.setItem(STORAGE_KEY, m); } catch { /* stockage indisponible */ }
    setModeState(m);
  }, []);

  return <ThemeContext.Provider value={{ mode, dark, setMode }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}

const ICON: Record<ThemeMode, JSX.Element> = {
  light: <path d="M12 4V2M12 22v-2M4 12H2M22 12h-2M5.6 5.6 4.2 4.2M19.8 19.8l-1.4-1.4M5.6 18.4l-1.4 1.4M19.8 4.2l-1.4 1.4M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z" />,
  dark: <path d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11Z" />,
  system: <path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1ZM8 21h8M12 17v4" />,
};
const Icon = ({ mode, className = 'h-4 w-4' }: { mode: ThemeMode; className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>{ICON[mode]}</svg>
);

/** Bouton de thème (icône soleil / lune) avec menu Clair / Sombre / Système. `tone` comme LanguageSwitcher. */
export function ThemeSwitcher({ tone = 'light', className = '' }: { tone?: 'light' | 'dark'; className?: string }) {
  const { mode, dark, setMode } = useTheme();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const label = { light: t('theme_light'), dark: t('theme_dark'), system: t('theme_system') };
  const btn = tone === 'dark'
    ? 'bg-white/10 hover:bg-white/20 border-white/25 text-white backdrop-blur-xl'
    : 'bg-white hover:bg-gray-50 border-gray-200 text-gray-700';

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('theme_label')} : ${label[mode]}`}
        title={`${t('theme_label')} : ${label[mode]}`}
        className={`inline-flex h-[30px] w-[34px] items-center justify-center rounded-lg border transition-colors ${btn}`}
      >
        <Icon mode={dark ? 'dark' : 'light'} />
      </button>
      {open && (
        <ul role="menu" className="absolute right-0 z-[300] mt-1.5 min-w-[160px] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl">
          {MODES.map(m => (
            <li key={m}>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={mode === m}
                onClick={() => { setMode(m); setOpen(false); }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors ${mode === m ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                <Icon mode={m} />
                <span className="flex-1 text-left">{label[m]}</span>
                {mode === m && <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
